import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { TABLE_IDS } from '@/components/DataTable/tableIds';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  AUDIT_LOGS_FILTERABLE_FIELDS,
  type AuditLog,
} from '@kirin/api-client/contracts/audit-logs-table';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { createAuditLogsColumns } from './columns';
import { fetchOrganizationAuditLogs } from './api';

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams
): readonly unknown[] {
  return [
    'auditLogs',
    'dataTable',
    {
      organizationId,
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field,
      sortOrder: params.sort?.order,
      search: params.search,
    },
  ];
}

/** Organization audit-logs DataTable, keyed per organization to reset state. */
export function DataTableAuditLogsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  // Read the per-user Timestamp Format so the Timestamp column picks a
  // format-aware width; recreate the columns when the format toggles.
  const { value: timestampFormat } = useTimestampFormat();
  const columns = useMemo(
    () => createAuditLogsColumns(organizationId, timestampFormat),
    [organizationId, timestampFormat]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchOrganizationAuditLogs(organizationId, params, signal),
    [organizationId]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => getQueryKeysFromParams(organizationId, params),
    [organizationId]
  );

  // Include organizationId in countScope so knownTotal cache is invalidated
  // when the user navigates between organizations.
  const countScope = useMemo(() => ({ organizationId }), [organizationId]);

  return (
    <DataTable<AuditLog>
      id={TABLE_IDS.ORGANIZATION.AUDIT_LOGS}
      mode="server"
      className="flex flex-col h-[calc(100vh-11rem)]"
      containerClassName="flex-1"
      columns={columns}
      search={{
        placeholder: 'Search audit logs',
        className: 'w-[260px]',
        inputProps: { 'aria-label': 'Search audit logs' },
      }}
      features={{ enableSorting: true }}
      initialState={{
        sort: {
          field: AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP,
          order: SortOrder.Desc,
        },
      }}
      queryFn={queryFn}
      getQueryKeysFromParams={queryKeyFn}
      countScope={countScope}
      noDataLabel="No audit logs found."
      errorBoundaryLabel="Something went wrong in the Audit Logs table"
      queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
    />
  );
}

DataTableAuditLogsContent.displayName = 'DataTableAuditLogsContent';

export default function DataTableAuditLogsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableAuditLogsContent
      key={organizationId}
      organizationId={organizationId}
    />
  );
}
